import moment, { type Moment } from 'moment';

import type { PeriodDateType, PeriodMaxSpan } from './RelativeAbsolutePeriodFilter.types';

const MAX_RELATIVE_AMOUNT_SCAN = 1000;

const toGranularity = (dateType: PeriodDateType): moment.unitOfTime.StartOf => dateType;

/**
 * True when the calendar days from `start` to `end`, both included, cover at most `maxSpan`.
 * Same rule as the backend: `end <= start + maxSpan - 1 day` (3 months: Jul 1 → Sep 30 at most).
 */
export const isSpanWithinLimit = (start: Moment, end: Moment, maxSpan: PeriodMaxSpan): boolean =>
  !end
    .clone()
    .startOf('day')
    .isAfter(
      start.clone().startOf('day').add(maxSpan.amount, maxSpan.unit).subtract(1, 'day'),
    );

/**
 * Largest N such that the relative window fits `maxSpan` after its inclusive start is trimmed.
 * 0 means even one unit is too long (e.g. "1 quarter" against a 3 month limit).
 */
export const getMaxRelativeAmount = (
  dateType: PeriodDateType,
  maxSpan: PeriodMaxSpan,
  now: Moment = moment(),
): number => {
  const end = now.clone().endOf('day');
  let amount = 0;

  while (
    amount < MAX_RELATIVE_AMOUNT_SCAN &&
    isSpanWithinLimit(end.clone().subtract(amount + 1, dateType), end, maxSpan)
  ) {
    amount += 1;
  }

  // A relative amount equal to the cap unit can still use the full cap: the query
  // resolver trims the extra inclusive boundary day from the start of the range.
  return dateType === maxSpan.unit ? Math.max(amount, maxSpan.amount) : amount;
};

/** Units that can still be picked in relative mode (at least "1 unit" fits the limit). */
export const getAllowedDateTypes = (
  dateTypes: readonly PeriodDateType[],
  maxSpan: PeriodMaxSpan | undefined,
  now?: Moment,
): readonly PeriodDateType[] =>
  maxSpan ? dateTypes.filter(type => getMaxRelativeAmount(type, maxSpan, now) >= 1) : dateTypes;

/**
 * Whether `candidate` can be the second end of a range that started (or ends) at `anchor`,
 * counting the whole `dateType` unit of each end, as the report expands them.
 */
export const isCandidateOutsideSpan = (
  candidate: Moment,
  anchor: Moment,
  dateType: PeriodDateType,
  maxSpan: PeriodMaxSpan,
): boolean => {
  const [first, last] = candidate.isBefore(anchor) ? [candidate, anchor] : [anchor, candidate];
  const unit = toGranularity(dateType);

  return !isSpanWithinLimit(first.clone().startOf(unit), last.clone().endOf(unit), maxSpan);
};

/** Whether an absolute range, with each end expanded to its whole `dateType` unit, fits `maxSpan`. */
export const isRangeWithinSpan = (
  range: [Moment, Moment],
  dateType: PeriodDateType,
  maxSpan: PeriodMaxSpan,
): boolean => !isCandidateOutsideSpan(range[1], range[0], dateType, maxSpan);

/** Pulls the start of an over-long absolute range forward so it fits `maxSpan`. */
export const clampRangeToSpan = (
  range: [Moment, Moment],
  dateType: PeriodDateType,
  maxSpan: PeriodMaxSpan,
): [Moment, Moment] => {
  if (isRangeWithinSpan(range, dateType, maxSpan)) return range;

  const unit = toGranularity(dateType);
  const end = range[1].clone().endOf(unit);
  let start = end
    .clone()
    .startOf('day')
    .subtract(maxSpan.amount, maxSpan.unit)
    .add(1, 'day')
    .startOf(unit);

  // Month ends and rounding down to the unit can overshoot the cap: step forward until it fits.
  while (!isSpanWithinLimit(start, end, maxSpan)) {
    const nextStart = start.clone().add(1, dateType).startOf(unit);
    if (!nextStart.isBefore(end)) {
      // A whole unit such as a year cannot fit a shorter cap. Keep the end and clip by day.
      start = end.clone().startOf('day').subtract(maxSpan.amount, maxSpan.unit).add(1, 'day');
      break;
    }
    start = nextStart;
  }

  return [start, range[1]];
};
